import { useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ActionCreators } from 'redux-undo'
import { useHotkeys } from 'react-hotkeys-hook'
import { createPortal } from 'react-dom'
import useOnOutsideClick from 'hooks/useOnOutsideClick'
import {
  pdfFileSelector,
  elementsSelector,
  pagesSelector,
} from 'apps/studio/store/selectors/pdf'
import { folderIdSelector } from 'apps/formsCreationKit/store/selectors/settings'
import {
  pastEntitiesSelector,
  futureEntitiesSelector,
} from 'apps/formsCreationKit/store/selectors/entities'
import {
  undoHotkeyCombos,
  undoHotkeyShortcutText,
  redoHotkeyCombos,
  redoHotkeyShortcutText,
} from 'apps/formsCreationKit/lib/constants'
import { resetPdf, replacePdf } from 'apps/studio/store/actions/sharedActions'
import {
  studioSettingsActions,
  INTRO_MODE,
} from 'apps/studio/store/reducers/settings/studioSettings'
const { setActiveMode } = studioSettingsActions
import { appSettingsActions } from 'apps/formsCreationKit/store/reducers/settings/appSettings'
const { updateFlashMessage } = appSettingsActions
import { PDFDocument } from 'pdf-lib'
import {
  convertPdftoBlobUrl,
  getDocumentBuffer,
} from 'apps/studio/lib/utils/fileUploads'
import { PdfLoaderInput } from 'apps/studio/components/intro/pdfLoader'
import { CancelConfirmDialog } from 'apps/studio/components/dialogs/CancelConfirmDialog'
import { pdfPageMetaSelector } from 'apps/studio/store/selectors/pdfPageMeta'
import {
  ChevronDownIcon,
  ChevronUpIcon,
  ArrowLeftIcon,
  MenuIcon,
  GcLogoIcon,
  RedoIcon,
  UndoIcon,
  FormIcon,
  ClearIcon,
  UploadIcon,
} from '@gocanvas/gcds/icons'

export const FormDropdown = ({ plgDemo = false }) => {
  const [menuOpen, menuToggle] = useState(false)
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 })
  const [pendingReplace, setPendingReplace] = useState(null)
  const hasPdf = useSelector(pdfFileSelector)
  const pdfElements = useSelector(elementsSelector)
  const pdfPages = useSelector(pagesSelector)
  const pdfPageMeta = useSelector(pdfPageMetaSelector)
  const folder_id = useSelector(folderIdSelector)
  const toggleRef = useRef(null)
  const dropdownRef = useRef()
  const dispatch = useDispatch()

  useOnOutsideClick(dropdownRef, ({ target }) => {
    const clickedButton = toggleRef.current.contains(target)

    if (!clickedButton) {
      menuToggle(false)
    }
  })

  const undoable = useSelector(pastEntitiesSelector).length > 0
  const redoable = useSelector(futureEntitiesSelector).length > 0
  const undo = () => dispatch(ActionCreators.undo())
  const redo = () => dispatch(ActionCreators.redo())

  useHotkeys(undoHotkeyCombos, e => {
    e.preventDefault()
    undo()
  })
  useHotkeys(redoHotkeyCombos, e => {
    e.preventDefault()
    redo()
  })

  const handleExitClick = () => {
    if (plgDemo) {
      window.location.href = window.location.origin
      return
    }
    if (folder_id) {
      const exitUrl = `/forms?folder_id=${folder_id}`
      window.location.href = exitUrl
    } else {
      window.location.href = '/forms'
    }
  }

  const toggleClickHandler = () => {
    if (!menuOpen && toggleRef.current) {
      // Position the dropdown relative to the toggle button
      const rect = toggleRef.current.getBoundingClientRect()
      setDropdownPosition({ top: rect.bottom, left: rect.left })
    }
    menuToggle(!menuOpen)
  }

  const handleReplacePdfFileSelect = async file => {
    let blobUrl, buffer
    try {
      buffer = await getDocumentBuffer(file)
      blobUrl = await convertPdftoBlobUrl(file, buffer)
    } catch (error) {
      dispatch(updateFlashMessage({ message: error.message, type: 'error' }))
      return
    }

    let newPageCount, newPageDimensions
    try {
      const pdfDoc = await PDFDocument.load(buffer)
      const pages = pdfDoc.getPages()
      newPageCount = pages.length
      newPageDimensions = pages.map((page, i) => {
        const { width, height } = page.getSize()
        return {
          pageNumber: i + 1,
          originalWidth: width,
          originalHeight: height,
        }
      })
    } catch (error) {
      dispatch(
        updateFlashMessage({
          message: 'Could not read PDF dimensions',
          type: 'error',
        })
      )
      return
    }

    const removedPageIds = []
    const removedSectionIds = []
    pdfPages.forEach(p => {
      if (p.page_number > newPageCount) {
        removedSectionIds.push(...p.section_ids)
        removedPageIds.push(p.id)
      }
    })
    const removedEntryGuids = pdfElements.reduce((acc, el) => {
      if (el.page_number > newPageCount) acc.push(el.entry_guid)
      return acc
    }, [])

    const pendingData = {
      pdf_path: blobUrl,
      fileName: file.name,
      newPageCount,
      newPageDimensions,
      removedPageIds,
      removedSectionIds,
      removedEntryGuids,
      addedPages: newPageCount > pdfPages.length,
    }

    const dimensionsChanged =
      pdfPageMeta.length === 0 ||
      newPageCount !== pdfPageMeta.length ||
      newPageDimensions.some(newDim => {
        const existing = pdfPageMeta.find(
          m => m.pageNumber === newDim.pageNumber
        )
        if (!existing) return true
        return (
          Math.abs(newDim.originalWidth - existing.originalWidth) > 1 ||
          Math.abs(newDim.originalHeight - existing.originalHeight) > 1
        )
      })

    if (dimensionsChanged) {
      window.pendo?.track('replace_pdf_warning_displayed', {
        fileName: file.name,
      })
      setPendingReplace(pendingData)
    } else {
      dispatch(replacePdf(pendingData))
    }
  }

  const formDropdownSections = [
    {
      label: 'Edit',
      items: [
        {
          label: 'Undo',
          icon: <UndoIcon className="dropdown-icon" />,
          formAction: () => {
            if (undoable) {
              undo()
            }
          },
          shortcut: undoHotkeyShortcutText,
          isDisabled: !undoable,
        },
        {
          label: 'Redo',
          icon: <RedoIcon className="dropdown-icon" />,
          formAction: () => {
            if (redoable) {
              redo()
            }
          },
          shortcut: redoHotkeyShortcutText,
          isDisabled: !redoable,
        },
      ],
    },
    {
      label: 'PDF',
      items: [
        {
          label: 'Start Over',
          icon: <FormIcon className="dropdown-icon" />,
          formAction: () => {
            dispatch(resetPdf({ deletePdf: true }))
            dispatch(setActiveMode({ activeMode: INTRO_MODE }))
          },
          isDisabled: !hasPdf,
        },
        {
          label: 'Replace PDF',
          icon: <UploadIcon className="dropdown-icon" />,
          formAction: () =>
            document.getElementById('replace-pdf--input').click(),
          isDisabled: !hasPdf,
        },
        {
          label: 'Reset',
          icon: <ClearIcon className="dropdown-icon" />,
          formAction: () => dispatch(resetPdf({})),
          isDisabled: !hasPdf || (pdfElements && pdfElements.length === 0),
        },
        {
          label: 'Exit',
          icon: <ArrowLeftIcon className="dropdown-icon" />,
          formAction: handleExitClick,
        },
      ],
    },
  ]

  return (
    <div className="form-dropdown-container">
      <button
        type="button"
        className="gcds--button button--tertiary"
        ref={toggleRef}
        onClick={toggleClickHandler}
      >
        <MenuIcon className="icon--leading" />
        {menuOpen ? (
          <ChevronUpIcon className="icon--trailing" />
        ) : (
          <ChevronDownIcon className="icon--trailing" />
        )}
      </button>
      <PdfLoaderInput
        inputId="replace-pdf--input"
        onFileSelect={handleReplacePdfFileSelect}
      />
      {pendingReplace && (
        <CancelConfirmDialog
          title="Page Size Mismatch"
          description={`The page size or page count of this document doesn't match your original file. Fields may be misaligned, and any fields on pages that are no longer included will be removed. You can close without saving and upload a new document, or adjust your fields to fit the updated file.`}
          cancelText="Close Without Saving"
          confirmText="Save and Adjust Fields"
          onConfirm={() => {
            dispatch(replacePdf(pendingReplace))
            setPendingReplace(null)
          }}
          onCancel={() => setPendingReplace(null)}
        />
      )}
      {menuOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            className="form-dropdown-menu--portal gcds--control-panel"
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
            }}
          >
            <ul className="dropdown--menu">
              {formDropdownSections.map((section, sectionIndex) => (
                <React.Fragment key={`section-${sectionIndex}`}>
                  <li className="panel--section-title menu--section-label">
                    {section.label}
                  </li>
                  {section.items.map((item, itemIndex) => (
                    <li
                      key={`option-${sectionIndex}-${itemIndex}`}
                      className={`gcds--item action--item ${
                        item.isDisabled ? 'is-disabled' : ''
                      }`}
                    >
                      <button
                        className="menu--action-button"
                        onClick={() => {
                          if (item.isDisabled) return
                          menuToggle(false)
                          item.formAction()
                        }}
                        disabled={item.isDisabled}
                        {...(item.additionalProps || {})}
                      >
                        <div className="item--icon action--icon">
                          {item.icon}
                        </div>
                        <span className="item--label action--label">
                          {item.label}
                        </span>
                        {item.shortcut && (
                          <span className="item--label action--label action--shortcut">
                            {item.shortcut}
                          </span>
                        )}
                      </button>
                    </li>
                  ))}
                </React.Fragment>
              ))}
            </ul>
          </div>,
          document.body
        )}
    </div>
  )
}
